import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, Input, InjectionToken, inject, EventEmitter, ChangeDetectionStrategy, Output, Injectable, Directive, NgModule } from '@angular/core';
import { MXBaseComponent } from '@acorex/components/common';
import * as i1 from '@angular/cdk/portal';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { asyncScheduler, BehaviorSubject } from 'rxjs';
import { observeOn, finalize } from 'rxjs/operators';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';

class AXLoadingSpinnerComponent {
    constructor() {
        this.color = 'ghost';
        this.size = 16;
        this.stroke = 3;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXLoadingSpinnerComponent, selector: "ax-loading-spinner", inputs: { color: "color", size: "size", stroke: "stroke" }, ngImport: i0, template: `
    <span
      class="ax-loader ax-{{ color }}"
      [style.width.px]="size"
      [style.height.px]="size"
      [style.border-width.px]="stroke"
      [style.border-color]="color"
    ></span>
    @if (text) {
      <span> {{ text }} </span>
    }
  `, isInline: true, styles: ["ax-loading-spinner .ax-loader{animation:rotation .5s linear infinite;box-sizing:border-box;display:inline-block;height:3rem;width:3rem;border-radius:9999px;border-width:6px;border-style:solid;border-color:rgba(var(--ax-color-ghost-fore));border-bottom-color:transparent!important}ax-loading-spinner .ax-loader.ax-primary{border-color:rgba(var(--ax-color-primary-500))}ax-loading-spinner .ax-loader.ax-secondary{border-color:rgba(var(--ax-color-secondary-500))}ax-loading-spinner .ax-loader.ax-success{border-color:rgba(var(--ax-color-success-500))}ax-loading-spinner .ax-loader.ax-warning{border-color:rgba(var(--ax-color-warning-500))}ax-loading-spinner .ax-loader.ax-danger{border-color:rgba(var(--ax-color-danger-500))}ax-loading-spinner .ax-loader.ax-info{border-color:rgba(var(--ax-color-info-500))}ax-loading-spinner .ax-loader.ax-white{border-color:rgba(var(--ax-color-white))}ax-loading-spinner .ax-loader.ax-black{border-color:rgba(var(--ax-color-black))}@keyframes rotation{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.ax-dark .ax-loader.ax-primary{border-color:rgba(var(--ax-color-primary-200))}.ax-dark .ax-loader.ax-secondary{border-color:rgba(var(--ax-color-secondary-200))}.ax-dark .ax-loader.ax-success{border-color:rgba(var(--ax-color-success-200))}.ax-dark .ax-loader.ax-warning{border-color:rgba(var(--ax-color-warning-200))}.ax-dark .ax-loader.ax-danger{border-color:rgba(var(--ax-color-danger-200))}.ax-dark .ax-loader.ax-info{border-color:rgba(var(--ax-color-info-200))}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-loading-spinner', template: `
    <span
      class="ax-loader ax-{{ color }}"
      [style.width.px]="size"
      [style.height.px]="size"
      [style.border-width.px]="stroke"
      [style.border-color]="color"
    ></span>
    @if (text) {
      <span> {{ text }} </span>
    }
  `, encapsulation: ViewEncapsulation.None, styles: ["ax-loading-spinner .ax-loader{animation:rotation .5s linear infinite;box-sizing:border-box;display:inline-block;height:3rem;width:3rem;border-radius:9999px;border-width:6px;border-style:solid;border-color:rgba(var(--ax-color-ghost-fore));border-bottom-color:transparent!important}ax-loading-spinner .ax-loader.ax-primary{border-color:rgba(var(--ax-color-primary-500))}ax-loading-spinner .ax-loader.ax-secondary{border-color:rgba(var(--ax-color-secondary-500))}ax-loading-spinner .ax-loader.ax-success{border-color:rgba(var(--ax-color-success-500))}ax-loading-spinner .ax-loader.ax-warning{border-color:rgba(var(--ax-color-warning-500))}ax-loading-spinner .ax-loader.ax-danger{border-color:rgba(var(--ax-color-danger-500))}ax-loading-spinner .ax-loader.ax-info{border-color:rgba(var(--ax-color-info-500))}ax-loading-spinner .ax-loader.ax-white{border-color:rgba(var(--ax-color-white))}ax-loading-spinner .ax-loader.ax-black{border-color:rgba(var(--ax-color-black))}@keyframes rotation{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.ax-dark .ax-loader.ax-primary{border-color:rgba(var(--ax-color-primary-200))}.ax-dark .ax-loader.ax-secondary{border-color:rgba(var(--ax-color-secondary-200))}.ax-dark .ax-loader.ax-success{border-color:rgba(var(--ax-color-success-200))}.ax-dark .ax-loader.ax-warning{border-color:rgba(var(--ax-color-warning-200))}.ax-dark .ax-loader.ax-danger{border-color:rgba(var(--ax-color-danger-200))}.ax-dark .ax-loader.ax-info{border-color:rgba(var(--ax-color-info-200))}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], size: [{
                type: Input
            }], stroke: [{
                type: Input
            }] } });

const AX_LOADING_CONFIG = new InjectionToken('AX_LOADING_CONFIG', {
    providedIn: 'root',
    factory: () => AXLoadingDefaultConfig,
});
const AXLoadingDefaultConfig = {
    spinner: AXLoadingSpinnerComponent,
};
function loadingConfig(config = {}) {
    const result = {
        ...AXLoadingDefaultConfig,
        ...config,
    };
    return result;
}

class AXLoadingComponent extends MXBaseComponent {
    constructor() {
        super(...arguments);
        this.config = inject(AX_LOADING_CONFIG);
        this.visibleChange = new EventEmitter();
        this.type = 'spinner';
    }
    get visible() {
        return this._visible;
    }
    set visible(v) {
        if (v != this._visible) {
            this._visible = v;
            v ? this.getHostElement().classList.remove('ax-hide') : this.getHostElement().classList.add('ax-hide');
            this.visibleChange.emit(v);
        }
    }
    ngOnInit() {
        super.ngOnInit();
        this._selectedPortal = new ComponentPortal(this.config.spinner);
        this.cdr.markForCheck();
    }
    _handleAttched(ref) {
        ref = ref;
        if (ref.instance && this.context) {
            Object.assign(ref.instance, this.context);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXLoadingComponent, selector: "ax-loading", inputs: { visible: "visible", type: "type", context: "context" }, outputs: { visibleChange: "visibleChange" }, usesInheritance: true, ngImport: i0, template: ` <ng-template [cdkPortalOutlet]="_selectedPortal" (attached)="_handleAttched($event)"></ng-template> `, isInline: true, styles: ["ax-loading{position:relative}ax-loading:before,ax-loading:after{position:absolute;inset-inline-start:50%;top:50%;height:100%;width:100%;transform:translate(-50%);transform:translateY(-50%);transform:scale(0);border-radius:9999px;content:\"\"}ax-loading:before,ax-loading:after{background-color:transparent;animation:1s ease-in-out both ax-pulse;animation-duration:2s;animation-iteration-count:infinite}@keyframes ax-pulse{50%{transform:scale3d(1.05,1.05,1.05)}}ax-loading ax-loading-spinner{display:flex;gap:.75rem}.ax-loading-overlay-backdrop{background-color:rgba(var(--ax-color-surface));opacity:.85!important}.ax-loading-pane{height:100%!important;width:100%!important;bottom:0;inset-inline-end:0px;inset-inline-start:0px;top:0}.ax-loading-pane .cdk-dialog-container{display:flex!important;align-items:center;justify-content:center}.ax-loading-pane .cdk-dialog-container:focus,.ax-loading-pane .cdk-dialog-container:focus-visible,.ax-loading-pane .cdk-dialog-container:focus-within{outline:2px solid transparent;outline-offset:2px}\n"], dependencies: [{ kind: "directive", type: i1.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-loading', template: ` <ng-template [cdkPortalOutlet]="_selectedPortal" (attached)="_handleAttched($event)"></ng-template> `, changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, styles: ["ax-loading{position:relative}ax-loading:before,ax-loading:after{position:absolute;inset-inline-start:50%;top:50%;height:100%;width:100%;transform:translate(-50%);transform:translateY(-50%);transform:scale(0);border-radius:9999px;content:\"\"}ax-loading:before,ax-loading:after{background-color:transparent;animation:1s ease-in-out both ax-pulse;animation-duration:2s;animation-iteration-count:infinite}@keyframes ax-pulse{50%{transform:scale3d(1.05,1.05,1.05)}}ax-loading ax-loading-spinner{display:flex;gap:.75rem}.ax-loading-overlay-backdrop{background-color:rgba(var(--ax-color-surface));opacity:.85!important}.ax-loading-pane{height:100%!important;width:100%!important;bottom:0;inset-inline-end:0px;inset-inline-start:0px;top:0}.ax-loading-pane .cdk-dialog-container{display:flex!important;align-items:center;justify-content:center}.ax-loading-pane .cdk-dialog-container:focus,.ax-loading-pane .cdk-dialog-container:focus-visible,.ax-loading-pane .cdk-dialog-container:focus-within{outline:2px solid transparent;outline-offset:2px}\n"] }]
        }], propDecorators: { visibleChange: [{
                type: Output
            }], visible: [{
                type: Input
            }], type: [{
                type: Input
            }], context: [{
                type: Input
            }] } });

const DEFAULT_LOADER_ID = '_DEFAULT';
class AXLoadingService {
    constructor() {
        // WeakMap will remove components from itself upon
        // their garbage collection by JS runtime.
        this.loadingStates = new WeakMap();
        // Both loading state maps are kept in-sync such that
        // they can be used by both sync and async methods.
        this.loadingStates$ = new WeakMap();
        this.dialog = inject(Dialog);
    }
    show(config) {
        const ce = (config?.location ?? document.body);
        const dialogRef = this.dialog.open(AXLoadingComponent, {
            hasBackdrop: true,
            disableClose: true,
            restoreFocus: true,
            closeOnDestroy: true,
            closeOnNavigation: true,
            ariaModal: true,
            role: 'alertdialog',
            panelClass: ['ax-loading-pane', 'ax-animate-fadeIn'],
            backdropClass: ['ax-loading-overlay-backdrop', 'ax-animate-fadeIn'],
        });
        const id = new Date().getTime();
        dialogRef.componentInstance.visible = true;
        dialogRef.componentInstance.id = id.toString();
        if (ce != document.body) {
            const bg = dialogRef.overlayRef.backdropElement;
            const ov = dialogRef.overlayRef.overlayElement;
            ce.style.position = 'relative';
            ov.style.position = 'absolute';
            ce.appendChild(bg);
            ce.appendChild(ov);
            setTimeout(() => {
                ov.style.position = 'absolute';
            }, 0);
        }
        return id;
        // TODO: diabled focus and click, maybe we need a service and directive for it
    }
    hide(id) {
        const f = this.dialog.openDialogs.find((c) => c.componentInstance.id === id.toString());
        if (f) {
            f.close();
        }
    }
    doLoading(source$, context, loaderId) {
        this.startLoading(context, loaderId);
        return source$.pipe(observeOn(asyncScheduler), finalize(() => this.endLoading(context, loaderId)));
    }
    isLoading(context, loaderId) {
        const loaderStates = this.loadingStates.get(context);
        if (!loaderStates) {
            return false;
        }
        else {
            if (loaderId !== undefined) {
                return loaderStates.get(this.getLoaderId(loaderId)) ?? false;
            }
            else {
                return [...loaderStates.values()].filter((state) => state).length > 0;
            }
        }
    }
    isLoading$(context, loaderId) {
        const coalescedLoaderId = this.getLoaderId(loaderId);
        if (!this.hasLoadingStates(context, coalescedLoaderId)) {
            this.setLoadingState(context, false, loaderId);
        }
        return this.loadingStates$.get(context).get(coalescedLoaderId);
    }
    startLoading(context, loaderId) {
        this.setLoadingState(context, true, this.getLoaderId(loaderId));
    }
    endLoading(context, loaderId) {
        this.setLoadingState(context, false, this.getLoaderId(loaderId));
    }
    clearLoadings() {
        this.loadingStates = new WeakMap();
        this.loadingStates$ = new WeakMap();
    }
    setLoadingState(context, state, loaderId) {
        if (!this.hasLoadingStates(context, loaderId)) {
            if (this.hasContextLoadingState(context)) {
                this.loadingStates.get(context).set(loaderId, state);
                this.loadingStates$.get(context).set(loaderId, new BehaviorSubject(state));
            }
            else {
                this.loadingStates.set(context, new Map([[loaderId, state]]));
                this.loadingStates$.set(context, new Map([[loaderId, new BehaviorSubject(state)]]));
            }
        }
        else {
            this.loadingStates.get(context).set(loaderId, state);
            this.loadingStates$.get(context).get(loaderId).next(state);
        }
    }
    hasLoadingStates(context, loaderId) {
        return this.hasContextLoadingState(context) && this.hasLoaderLoadingState(context, loaderId);
    }
    hasContextLoadingState(context) {
        return this.loadingStates.has(context) && this.loadingStates$.has(context);
    }
    hasLoaderLoadingState(context, loaderId) {
        return this.loadingStates.get(context).has(loaderId) && this.loadingStates$.get(context).has(loaderId);
    }
    getLoaderId(loaderId) {
        return loaderId ?? DEFAULT_LOADER_ID;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingService, decorators: [{
            type: Injectable
        }] });

// This directive places an overlay with a loading spinner over its host element
// if isLoading equals to true and hides the overlay when isLoading becomes false.
class AXLoadingDirective {
    get isLoading() {
        return this._isLoading;
    }
    set isLoading(v) {
        this._isLoading = v;
        if (v) {
            this._loadingRef = this.loadingService.show({
                location: this._hostElement,
            });
        }
        else {
            this.loadingService.hide(this._loadingRef);
        }
    }
    constructor(elementRef, loadingService) {
        this.elementRef = elementRef;
        this.loadingService = loadingService;
        this._hostElement = this.elementRef.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingDirective, deps: [{ token: i0.ElementRef }, { token: AXLoadingService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.2", type: AXLoadingDirective, selector: "[axIsLoading]", inputs: { isLoading: ["axIsLoading", "isLoading"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[axIsLoading]',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: AXLoadingService }], propDecorators: { isLoading: [{
                type: Input,
                args: ['axIsLoading']
            }] } });

const COMPONENT = [AXLoadingComponent, AXLoadingDirective, AXLoadingSpinnerComponent];
const MODULES = [CommonModule, OverlayModule, DialogModule, PortalModule];
class AXLoadingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingModule, declarations: [AXLoadingComponent, AXLoadingDirective, AXLoadingSpinnerComponent], imports: [CommonModule, OverlayModule, DialogModule, PortalModule], exports: [AXLoadingComponent, AXLoadingDirective, AXLoadingSpinnerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingModule, providers: [AXLoadingService], imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLoadingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENT],
                    imports: [...MODULES],
                    exports: [...COMPONENT],
                    providers: [AXLoadingService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXLoadingComponent, AXLoadingDefaultConfig, AXLoadingDirective, AXLoadingModule, AXLoadingService, AXLoadingSpinnerComponent, AX_LOADING_CONFIG, loadingConfig };
